import {get, patch, post} from './http';
import {type QcSubmissionAttributes} from './qcReview';
import {type Submission} from './submissions';

export type QaReviewStatus = 'pending' | 'reviewed';
export type QaReviewOutcome = 'true_positive' | 'false_positive';

export type QaPolicyViolationQueueSummary = {
  policy_violation: string;
  pending: number;
  reviewed: number;
};

export type QaReviewItem = {
  id: string;
  submission_id: string;
  qa_config_id: string | null;
  policy_violation: string;
  classification_id: string | null;
  analyst_email: string;
  original_policy_violation: string;
  original_risk: string;
  status: QaReviewStatus;
  outcome: QaReviewOutcome | null;
  review_notes: string | null;
  // The SME's corrected classification - only ever set alongside outcome: 'false_positive'. Null for
  // true positives, since the correct call trivially equals original_policy_violation/original_risk there.
  // When qa_no_risk is true, the item wasn't a risk at all - qa_policy_violation/qa_risk stay null.
  qa_policy_violation: string | null;
  qa_risk: string | null;
  qa_no_risk: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  acknowledged_at: string | null;
  created_at: string;
  item_external_id: string | null;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  current_delivery_state: string;
  current_platform_status: string | null;
  platform: string;
  author_handle: string | null;
  author_name: string | null;
  published_at: string | null;
  metadata: Record<string, unknown>;
  // What the analyst submitted (notes, every risk, enrichment), read live off the submission - the same
  // shape QC review shows.
  submission: QcSubmissionAttributes;
  // The submission's latest platform re-check, under the Submissions grid's own names.
  status_check: Pick<
    Submission,
    | 'platform_status'
    | 'platform_feedback_message'
    | 'platform_reason_code'
    | 'platform_changes'
    | 'last_status_check_at'
  >;
  has_calibration_notes: boolean;
};

export type QaReviewQuery = {
  policy_violation?: string;
  status?: QaReviewStatus;
  page?: number;
  page_size?: number;
};

export type SubmitQaReviewPayload = {
  outcome: QaReviewOutcome;
  notes?: string;
  // Required by the server when outcome is 'false_positive', unless qa_no_risk is set.
  qa_policy_violation?: string;
  qa_risk?: string;
  qa_no_risk?: boolean;
};

export type QaAccuracyQuery = {
  from?: string;
  to?: string;
  analyst?: string;
  policy_violation?: string;
  // Splits the rows into one per week or month of submission, each with a period_start.
  interval?: 'week' | 'month';
};

export type QaVerticalAccuracy = {
  policy_violation: string;
  period_start: string | null;
  total_reviewed: number;
  true_positive: number;
  false_positive: number;
  accuracy_rate: number;
};

export type QaAnalystAccuracy = {
  analyst_email: string;
  period_start: string | null;
  total_reviewed: number;
  true_positive: number;
  false_positive: number;
  accuracy_rate: number;
};

export const qaReviewApi = {
  policyViolations: async () =>
    get<QaPolicyViolationQueueSummary[]>('/qa-review/policy-violations'),

  // The backend returns a bare page of results (no total-count envelope) - Skip/Take server-side
  // with no way to know how many pages remain, which is fine for the small weekly sample sizes this
  // queue deals with.
  list: async (parameters?: QaReviewQuery) => get<QaReviewItem[]>('/qa-review', parameters),

  get: async (id: string) => get<QaReviewItem>(`/qa-review/${id}`),

  submitReview: async (id: string, payload: SubmitQaReviewPayload) =>
    patch<SubmitQaReviewPayload, QaReviewItem>(`/qa-review/${id}`, payload),

  accuracy: async (parameters?: QaAccuracyQuery) =>
    get<QaAnalystAccuracy[]>('/qa-review/accuracy', parameters),

  verticalAccuracy: async (parameters?: Pick<QaAccuracyQuery, 'from' | 'to' | 'interval'>) =>
    get<QaVerticalAccuracy[]>('/qa-review/accuracy/verticals', parameters),

  // Scoped server-side to the calling analyst's own unacknowledged false-positive feedback - no
  // query params needed.
  myFeedback: async () => get<QaReviewItem[]>('/qa-review/my-feedback'),

  acknowledge: async (id: string) => post<undefined>(`/qa-review/${id}/acknowledge`)
};
